import React, { useCallback, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

import { LabeledField } from '@/__core/components/LabeledField';
import { Box } from '@mui/system';

export interface ItemAddOptionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { name: string }) => Promise<any>;
  loading?: boolean;
  title: string;
  label: string;
  placeholder: string;
}

export default function ItemAddOptionModal({
  open,
  onClose,
  onSave,
  loading = false,
  title,
  label,
  placeholder,
}: ItemAddOptionModalProps) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = loading || submitting;

  const handleClose = useCallback(() => {
    if (busy) return;
    setName('');
    setError(null);
    onClose();
  }, [busy, onClose]);

  const handleSave = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    try {
      setSubmitting(true);
      setError(null);
      await onSave({ name: trimmed });
      setName('');
      onClose();
    } catch (saveError: any) {
      setError(saveError?.message || 'Có lỗi xảy ra khi tạo mới');
    } finally {
      setSubmitting(false);
    }
  }, [name, onSave, onClose]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleSave();
    }
  }, [handleSave]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid #e5e7eb',
          pb: 2,
          fontSize: '1.25rem',
          fontWeight: 600,
          color: '#111827',
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mt: 2 }}>
        <LabeledField label={label} error={!!error} helperText={error || undefined} labelWidth={120}>
          <TextField
            fullWidth
            placeholder={placeholder}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={handleKeyDown}
            error={!!error}
            disabled={busy}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
          />
        </LabeledField>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: '1px solid #e5e7eb',
          p: 2,
          gap: 2,
        }}
      >
        <Button
          onClick={handleClose}
          variant='outlined'
          disabled={busy}
          sx={{
            borderColor: '#d1d5db',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Hủy
        </Button>
        <Button
          onClick={() => {
            void handleSave();
          }}
          variant='contained'
          disabled={!name.trim() || busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
          sx={{
            backgroundColor: '#10b981',
            '&:hover': {
              backgroundColor: '#059669',
            },
            '&:disabled': {
              backgroundColor: '#d1d5db',
              color: '#9ca3af',
            },
          }}
        >
          {busy ? 'Đang tạo...' : 'Tạo mới'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
